import { DialogRef } from '@angular/cdk/dialog';
import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { catchError, finalize, map, of, switchMap, throwError } from 'rxjs';
import { CompanyNameType } from 'src/app/models/company.model';
import {
  CreateDeviceFromTemplateRequest,
  DeviceTemplateSummaryDto,
  DeviceUnitIdStrategy,
  DeviceUpdate,
} from 'src/app/models/device.model';
import { CertificateService } from 'src/app/services/certificate.service';
import { DeviceService } from 'src/app/services/device.service';
import { MasterAdminCompanyService } from 'src/app/services/master-admin-company.service';

interface DeviceTemplateOption extends DeviceTemplateSummaryDto {
  displayName: string;
}

interface DeviceUnitIdStrategyOption {
  value: DeviceUnitIdStrategy;
  labelKey: string;
}

const DEVICE_UNIT_ID_STRATEGY_LABEL_KEYS: Record<DeviceUnitIdStrategy, string> =
  {
    [DeviceUnitIdStrategy.FIRST_AVAILABLE]:
      'modal.create_device.strategy.first_available',
    [DeviceUnitIdStrategy.RANDOM_IN_RANGE]:
      'modal.create_device.strategy.random',
    [DeviceUnitIdStrategy.LARGEST_PLUS_ONE]:
      'modal.create_device.strategy.largest_plus_one',
    [DeviceUnitIdStrategy.EXPLICIT]: 'modal.create_device.strategy.explicit',
  };

@Component({
  selector: 'app-device-create-modal',
  templateUrl: './device-create-modal.component.html',
  styleUrls: ['./device-create-modal.component.scss'],
  standalone: false,
})
export class DeviceCreateModalComponent implements OnInit {
  private readonly certificateService = inject(CertificateService);
  private readonly deviceService = inject(DeviceService);
  private readonly masterAdminCompanyService = inject(
    MasterAdminCompanyService,
  );
  private readonly formBuilder = inject(FormBuilder);
  private readonly translateService = inject(TranslateService);
  private readonly dialogRef = inject<DialogRef<string | false>>(DialogRef);

  templates: DeviceTemplateOption[] = [];
  companies: CompanyNameType[] = [];
  readonly unitIdStrategies: DeviceUnitIdStrategyOption[] = Object.values(
    DeviceUnitIdStrategy,
  ).map((value) => ({
    value,
    labelKey: DEVICE_UNIT_ID_STRATEGY_LABEL_KEYS[value],
  }));
  selectedTemplate: DeviceTemplateOption | null = null;
  selectedCompany: CompanyNameType | null = null;
  loadingTemplates = false;
  loadingCompanies = false;
  creatingDevice = false;
  errorMessage = '';
  private createdDeviceId: string | null = null;
  readonly form = this.formBuilder.nonNullable.group({
    name: ['', [Validators.maxLength(160)]],
    description: ['', [Validators.maxLength(1024)]],
    companyId: ['', [Validators.maxLength(120)]],
    unitIdStrategy: [DeviceUnitIdStrategy.FIRST_AVAILABLE],
    unitId: [{ value: '', disabled: true }, [Validators.pattern(/^[1-9]\d*$/)]],
  });

  ngOnInit(): void {
    this.loadTemplates();
    this.loadCompanies();
  }

  loadTemplates(): void {
    if (this.loadingTemplates || this.creatingDevice) {
      return;
    }

    this.loadingTemplates = true;
    this.errorMessage = '';

    this.certificateService
      .listDeviceTemplates()
      .pipe(finalize(() => (this.loadingTemplates = false)))
      .subscribe({
        next: (response) => {
          this.templates = (response.templates ?? []).map((template) =>
            this.toTemplateOption(template),
          );
        },
        error: (error) => {
          this.templates = [];
          this.errorMessage = this.resolveErrorMessage(
            error,
            this.translateService.instant(
              'modal.create_device.error.load_templates',
            ),
          );
        },
      });
  }

  onTemplateSelected(value: DeviceTemplateOption | string): void {
    this.selectedTemplate =
      typeof value === 'string'
        ? (this.templates.find((template) => template.displayName === value) ??
          null)
        : value;
    this.errorMessage = '';
  }

  onCompanySelected(value: CompanyNameType | string): void {
    const company =
      typeof value === 'string' ? this.findCompanyByNameOrId(value) : value;

    this.selectedCompany = company ?? null;
    this.form.controls.companyId.setValue(this.selectedCompany?.id ?? '');
  }

  onUnitIdStrategySelected(value: DeviceUnitIdStrategyOption | string): void {
    const strategy =
      typeof value === 'string'
        ? this.unitIdStrategies.find(
            (option) =>
              option.value === value ||
              this.translateService.instant(option.labelKey) === value,
          )
        : value;

    if (!strategy) {
      return;
    }

    this.form.controls.unitIdStrategy.setValue(strategy.value);

    const unitIdControl = this.form.controls.unitId;
    if (strategy.value === DeviceUnitIdStrategy.EXPLICIT) {
      unitIdControl.enable({ emitEvent: false });
      unitIdControl.addValidators(Validators.required);
    } else {
      unitIdControl.removeValidators(Validators.required);
      unitIdControl.setValue('', { emitEvent: false });
      unitIdControl.disable({ emitEvent: false });
    }
    unitIdControl.updateValueAndValidity();
  }

  companyFormatter = (item: CompanyNameType | null, value: string): string =>
    item?.name ?? value;

  get isExplicitUnitIdStrategy(): boolean {
    return (
      this.form.controls.unitIdStrategy.value === DeviceUnitIdStrategy.EXPLICIT
    );
  }

  get hasCreatedDevice(): boolean {
    return this.createdDeviceId !== null;
  }

  get submitButtonTitle(): string {
    if (this.creatingDevice) {
      return this.hasCreatedDevice
        ? 'modal.create_device.updating'
        : 'modal.create_device.creating';
    }

    return this.hasCreatedDevice
      ? 'modal.create_device.retry_update'
      : 'modal.create_device.create';
  }

  confirm(): void {
    const templateId = this.selectedTemplate?.id?.trim();
    if (
      (!this.createdDeviceId && !templateId) ||
      this.form.invalid ||
      this.creatingDevice
    ) {
      return;
    }

    this.creatingDevice = true;
    this.errorMessage = '';

    const deviceId$ = this.createdDeviceId
      ? of(this.createdDeviceId)
      : this.certificateService
          .createDeviceFromTemplate(templateId!, this.buildCreateRequest())
          .pipe(
            map((device) => {
              const deviceId = device.deviceId?.trim();
              if (!deviceId) {
                throw new Error(
                  this.translateService.instant(
                    'modal.create_device.error.no_device_id',
                  ),
                );
              }

              this.createdDeviceId = deviceId;
              this.lockCreationOptions();
              return deviceId;
            }),
          );

    deviceId$
      .pipe(
        switchMap((deviceId) => {
          const payload = this.buildUpdatePayload();
          if (Object.keys(payload).length === 0) {
            return of(deviceId);
          }

          return this.deviceService.updateDevice(deviceId, payload).pipe(
            map(() => deviceId),
            catchError((error) =>
              throwError(
                () =>
                  new Error(
                    this.resolveErrorMessage(
                      error,
                      this.translateService.instant(
                        'modal.create_device.error.update',
                      ),
                    ),
                  ),
              ),
            ),
          );
        }),
        finalize(() => (this.creatingDevice = false)),
      )
      .subscribe({
        next: (deviceId) => {
          this.dialogRef.close(deviceId);
        },
        error: (error) => {
          this.errorMessage = this.resolveErrorMessage(
            error,
            this.translateService.instant('modal.create_device.error.create'),
          );
        },
      });
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  private loadCompanies(): void {
    this.loadingCompanies = true;
    this.masterAdminCompanyService
      .getAllCompanies()
      .pipe(finalize(() => (this.loadingCompanies = false)))
      .subscribe({
        next: (companies) => {
          this.companies = companies;
        },
        error: () => {
          this.companies = [];
        },
      });
  }

  private buildUpdatePayload(): DeviceUpdate {
    const name = this.form.controls.name.value;
    const description = this.form.controls.description.value;
    const companyId = this.form.controls.companyId.value.trim();
    const payload: DeviceUpdate = {};

    if (name.trim()) {
      payload.nameAlias = name;
    }
    if (description.trim()) {
      payload.description = description;
    }
    if (companyId) {
      payload.companyId = companyId;
    }

    return payload;
  }

  private buildCreateRequest(): CreateDeviceFromTemplateRequest {
    const strategy = this.form.controls.unitIdStrategy.value;
    if (strategy === DeviceUnitIdStrategy.EXPLICIT) {
      return {
        strategy,
        unitId: Number(this.form.controls.unitId.value),
      };
    }

    return { strategy };
  }

  private lockCreationOptions(): void {
    this.form.controls.unitIdStrategy.disable({ emitEvent: false });
    this.form.controls.unitId.disable({ emitEvent: false });
  }

  private findCompanyByNameOrId(value: string): CompanyNameType | undefined {
    const normalized = value.trim().toLowerCase();
    if (!normalized) {
      return undefined;
    }

    return this.companies.find(
      (company) =>
        company.id.trim().toLowerCase() === normalized ||
        company.name.trim().toLowerCase() === normalized,
    );
  }

  private toTemplateOption(
    template: DeviceTemplateSummaryDto,
  ): DeviceTemplateOption {
    const name =
      template.name?.trim() ||
      template.id?.trim() ||
      this.translateService.instant('modal.create_device.template');
    const suffix = template.id?.trim() ? ` (${template.id.trim()})` : '';

    return {
      ...template,
      displayName: `${name}${suffix}`,
    };
  }

  private resolveErrorMessage(error: unknown, fallback: string): string {
    if (!error) {
      return fallback;
    }
    if (typeof error === 'string') {
      return error;
    }
    if (typeof error === 'object' && error !== null) {
      const typedError = error as {
        message?: string;
        error?: { message?: string; title?: string };
      };
      if (typedError.error?.message) {
        return typedError.error.message;
      }
      if (typedError.error?.title) {
        return typedError.error.title;
      }
      if (typedError.message) {
        return typedError.message;
      }
    }
    return fallback;
  }
}
